<template>
  <div>
    <div v-if="loading" class="text-center text-gray-500 py-4">Loading detail...</div>

    <div v-else-if="!detail" class="text-center text-gray-500 py-4">No detail available.</div>

    <div v-else>
      <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <div class="text-sm font-semibold text-dark-primary">Expense #{{ detail.id }}</div>
        <div class="text-xs text-gray-500">Created {{ formatDate(detail.created_at) }}</div>
      </div>

      <!-- Proxy-submission banner -->
      <div
        v-if="isProxy"
        class="mb-3 p-2 rounded bg-blue-50 border border-blue-200 text-sm text-blue-900"
      >
        <span class="font-semibold">Entered by {{ fullName(detail.created_by) }}</span>
        on behalf of <span class="font-semibold">{{ fullName(detail.submitter) }}</span>.
        The record belongs to {{ fullName(detail.submitter) }} — they are the one who gets
        reimbursed, and their manager is the one who reviews it.
      </div>

      <!-- Rejection reason banner for rejected records -->
      <div
        v-if="detail.status === 'rejected' && detail.rejection_reason"
        class="mb-3 p-2 rounded bg-red-50 border border-red-200"
      >
        <div class="text-xs font-semibold text-red-700 mb-1">Rejection reason</div>
        <div class="text-sm text-red-700 whitespace-pre-wrap">{{ detail.rejection_reason }}</div>
      </div>

      <!-- Field grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div class="text-xs text-gray-500">Submitter</div>
          <div class="text-sm">{{ fullName(detail.submitter) }}</div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Submitted at</div>
          <div class="text-sm">{{ formatDateTime(detail.submitted_at) }}</div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Date of expense</div>
          <div class="text-sm">{{ formatDate(detail.date_of_expense) }}</div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Amount</div>
          <div class="text-sm font-semibold">${{ formatAmount(detail.amount) }}</div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Category</div>
          <div class="text-sm">{{ detail.category ? detail.category.name : '—' }}</div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Client reimbursable?</div>
          <div class="text-sm">
            <template v-if="detail.is_client_reimbursable">
              Yes — {{ detail.client_name || '—' }}<span v-if="detail.engagement_title"> / {{ detail.engagement_title }}</span>
            </template>
            <template v-else>No</template>
          </div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Paid by</div>
          <div class="text-sm">
            <template v-if="detail.payment_method === 'company_paid'">Company card / debit (bank reconciled, no Payroll)</template>
            <template v-else>Personal (reimbursed via Payroll)</template>
          </div>
        </div>
        <div>
          <div class="text-xs text-gray-500">Retro Expense?</div>
          <div class="text-sm">{{ detail.is_retro ? 'Yes' : 'No' }}</div>
        </div>
      </div>

      <div class="mb-3">
        <div class="text-xs text-gray-500">Description</div>
        <div class="text-sm">{{ detail.description }}</div>
      </div>

      <div v-if="detail.notes" class="mb-3">
        <div class="text-xs text-gray-500">Notes</div>
        <div class="text-sm whitespace-pre-wrap">{{ detail.notes }}</div>
      </div>

      <!-- Receipts -->
      <div class="mb-3">
        <div class="text-xs text-gray-500 mb-1">
          Receipts ({{ detail.receipts ? detail.receipts.length : 0 }})
        </div>
        <ul v-if="detail.receipts && detail.receipts.length > 0" class="space-y-1">
          <li
            v-for="r in detail.receipts"
            :key="r.id"
            class="flex items-center justify-between text-sm border border-gray-200 rounded px-2 py-1 bg-white"
          >
            <button
              type="button"
              class="text-accent-primary hover:underline truncate text-left"
              @click="viewingReceipt = r"
            >{{ r.original_filename || `Receipt #${r.id}` }}</button>
            <span class="text-xs text-gray-500 ml-2">{{ formatBytes(r.size_bytes) }}</span>
          </li>
        </ul>
        <div v-else class="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
          No receipt attached.
        </div>
      </div>

      <!-- Review checklist prompt -->
      <div
        v-if="allowApproval"
        class="mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-xs text-gray-700"
      >
        Review checklist: (1) is the expense business-related and legitimate, (2) is a receipt
        attached, (3) is the justification/notes adequate, (4) is client/engagement correct
        if flagged reimbursable.
      </div>

      <!-- Reject form (inline) -->
      <div
        v-if="showRejectForm"
        class="mb-3 p-3 rounded bg-red-50 border border-red-200"
      >
        <label class="block text-sm font-semibold text-red-700 mb-1">Rejection reason (required)</label>
        <textarea
          v-model="rejectReason"
          rows="3"
          class="w-full p-2 border border-red-300 rounded text-sm"
          placeholder="Explain what needs to change so the submitter can fix it."
        ></textarea>
        <div class="flex gap-2 justify-end mt-2">
          <button
            class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100"
            @click="cancelReject"
          >Cancel</button>
          <button
            class="text-sm px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            :disabled="!rejectReason.trim() || acting"
            @click="doReject"
          >{{ acting ? 'Working...' : 'Reject' }}</button>
        </div>
      </div>

      <!-- Action buttons -->
      <div v-if="(allowApproval || allowAccountingActions) && !showRejectForm" class="flex gap-2 justify-end">
        <button
          class="text-sm px-3 py-1.5 border border-red-500 text-red-700 rounded hover:bg-red-50"
          :disabled="acting"
          @click="showRejectForm = true"
        >Reject</button>
        <button
          class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
          :disabled="acting"
          @click="doApprove"
        >{{ acting ? 'Working...' : (allowAccountingActions ? 'Accounting Approve' : 'Approve') }}</button>
      </div>

      <!-- Admin overrides -->
      <div v-if="showAdminOverrides" class="mt-3 pt-3 border-t border-gray-300">
        <button
          class="text-xs text-gray-600 hover:text-dark-primary"
          @click="adminOpen = !adminOpen"
        >{{ adminOpen ? '▼' : '▶' }} Admin overrides</button>
        <div v-if="adminOpen" class="mt-2 p-3 rounded bg-yellow-50 border border-yellow-300 space-y-3">
          <div class="text-xs text-yellow-800">
            Overrides bypass the state machine and are fully audited. Use only to correct errors.
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div>
              <label class="block text-xs">Override status to:</label>
              <select v-model="admin.newStatus" class="p-1 border border-gray-400 rounded w-full">
                <option :value="null" disabled>Pick a status...</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="manager_approved">Manager Approved</option>
                <option value="accounting_approved">Accounting Approved</option>
                <option value="closed">Closed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div class="flex items-end">
              <button
                class="text-xs px-2 py-1 border border-yellow-600 text-yellow-900 rounded hover:bg-yellow-100"
                :disabled="!admin.newStatus || !admin.reason || acting"
                @click="doOverrideStatus"
              >Apply status override</button>
            </div>

            <div v-if="detail.payment_method !== 'company_paid'" class="flex items-end">
              <button
                class="text-xs px-2 py-1 border border-yellow-600 text-yellow-900 rounded hover:bg-yellow-100"
                :disabled="!detail.submitter_reimbursed || !admin.reason || acting"
                @click="doUnmarkReimbursed"
              >Unmark Reimbursed</button>
            </div>
            <div v-if="detail.payment_method === 'company_paid'" class="flex items-end">
              <button
                class="text-xs px-2 py-1 border border-yellow-600 text-yellow-900 rounded hover:bg-yellow-100"
                :disabled="!detail.bank_reconciled || !admin.reason || acting"
                @click="doUnmarkBankReconciled"
              >Unmark Bank Reconciled</button>
            </div>
            <div v-if="detail.is_client_reimbursable" class="flex items-end">
              <button
                class="text-xs px-2 py-1 border border-yellow-600 text-yellow-900 rounded hover:bg-yellow-100"
                :disabled="!detail.client_paid || !admin.reason || acting"
                @click="doUnmarkClientPaid"
              >Unmark Client Paid</button>
            </div>

            <div class="flex items-end">
              <button
                v-if="!detail.is_deleted"
                class="text-xs px-2 py-1 border border-red-600 text-red-800 rounded hover:bg-red-50"
                :disabled="!admin.reason || acting"
                @click="doSoftDelete"
              >Soft Delete</button>
              <button
                v-else
                class="text-xs px-2 py-1 border border-green-600 text-green-800 rounded hover:bg-green-50"
                :disabled="!admin.reason || acting"
                @click="doRestore"
              >Restore</button>
            </div>
          </div>

          <div>
            <label class="block text-xs">Reason (required for any override):</label>
            <textarea
              v-model="admin.reason"
              rows="2"
              class="w-full p-1 border border-gray-400 rounded text-sm"
              placeholder="What correction are you making and why?"
            ></textarea>
          </div>
        </div>
      </div>

      <div v-if="actionError" class="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
        {{ actionError }}
      </div>

      <ExpenseHistory :expenseId="expenseId" />
    </div>

    <ReceiptViewerModal :receipt="viewingReceipt" @close="viewingReceipt = null" />
  </div>
</template>

<script>
export default {
  name: "expense-review-panel",
};
</script>

<script setup>
import { ref, reactive, computed, defineProps, defineEmits } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  managerApproveExpense, managerRejectExpense,
  accountingApproveExpense, accountingRejectExpense,
  adminOverrideStatus, adminSoftDelete, adminRestore,
  adminUnmarkReimbursed, adminUnmarkClientPaid,
  adminUnmarkBankReconciled,
} from "@/services/Expense.js";
import ReceiptViewerModal from "./ReceiptViewerModal.vue";
import ExpenseHistory from "./ExpenseHistory.vue";

const props = defineProps({
  expenseId: { type: Number, required: true },
  detail: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  allowApproval: { type: Boolean, default: false },
  allowAccountingActions: { type: Boolean, default: false },
});

const emit = defineEmits(["approved", "rejected", "request-reload"]);

const adminOpen = ref(false);
const admin = reactive({ newStatus: null, reason: "" });

const viewingReceipt = ref(null);

const showAdminOverrides = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(p.isExpenseAdmin || p.isDev || p.isSuperuser);
});

const isProxy = computed(() => {
  if (!props.detail) return false;
  const cb = props.detail.created_by;
  const sub = props.detail.submitter;
  return !!(cb && sub && cb.id !== sub.id);
});

const store = useStore();
const $toast = useToast();

const acting = ref(false);
const actionError = ref("");
const showRejectForm = ref(false);
const rejectReason = ref("");

function fullName(u) {
  if (!u) return "—";
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

function formatDate(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString();
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}

function formatAmount(amount) {
  if (amount === null || amount === undefined) return "0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return num.toFixed(2);
}

function formatBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function extractErrorMessage(error) {
  if (error && error.response && error.response.data) {
    const data = error.response.data;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    if (data.reason) return data.reason;
    try { return JSON.stringify(data); } catch (e) { return String(data); }
  }
  return (error && error.message) || "Request failed.";
}

async function doApprove() {
  acting.value = true;
  actionError.value = "";
  const token = store.state.auth.token;
  try {
    const fn = props.allowAccountingActions ? accountingApproveExpense : managerApproveExpense;
    const updated = await fn(token, props.expenseId);
    $toast.open({ message: "Approved", type: "success", duration: 2000 });
    emit("approved", updated);
  } catch (error) {
    actionError.value = extractErrorMessage(error);
  }
  acting.value = false;
}

async function doReject() {
  const reason = rejectReason.value.trim();
  if (!reason) return;
  acting.value = true;
  actionError.value = "";
  const token = store.state.auth.token;
  try {
    const fn = props.allowAccountingActions ? accountingRejectExpense : managerRejectExpense;
    const updated = await fn(token, props.expenseId, reason);
    $toast.open({ message: "Rejected", type: "success", duration: 2000 });
    rejectReason.value = "";
    showRejectForm.value = false;
    emit("rejected", updated);
  } catch (error) {
    actionError.value = extractErrorMessage(error);
  }
  acting.value = false;
}

async function _runAdmin(fn, successMsg) {
  if (!admin.reason) return;
  acting.value = true;
  actionError.value = "";
  const token = store.state.auth.token;
  try {
    const updated = await fn(token);
    $toast.open({ message: successMsg, type: "success", duration: 2000 });
    admin.reason = "";
    admin.newStatus = null;
    emit("approved", updated);
  } catch (error) {
    actionError.value = extractErrorMessage(error);
  }
  acting.value = false;
}

async function doOverrideStatus() {
  if (!admin.newStatus || !admin.reason) return;
  await _runAdmin(
    (token) => adminOverrideStatus(token, props.expenseId, admin.newStatus, admin.reason),
    "Status overridden"
  );
}

async function doSoftDelete() {
  await _runAdmin(
    (token) => adminSoftDelete(token, props.expenseId, admin.reason),
    "Record soft-deleted"
  );
}

async function doRestore() {
  await _runAdmin(
    (token) => adminRestore(token, props.expenseId, admin.reason),
    "Record restored"
  );
}

async function doUnmarkReimbursed() {
  await _runAdmin(
    (token) => adminUnmarkReimbursed(token, props.expenseId, admin.reason),
    "Reimbursement unmarked"
  );
}

async function doUnmarkClientPaid() {
  await _runAdmin(
    (token) => adminUnmarkClientPaid(token, props.expenseId, admin.reason),
    "Client-paid unmarked"
  );
}

async function doUnmarkBankReconciled() {
  await _runAdmin(
    (token) => adminUnmarkBankReconciled(token, props.expenseId, admin.reason),
    "Bank-reconciled unmarked"
  );
}

function cancelReject() {
  rejectReason.value = "";
  showRejectForm.value = false;
  actionError.value = "";
}
</script>
